import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { API_BASE_URL, authFetch } from "../../config/api";

const emptyForm = {
  vendor: "",
  account_no: "",
  bill_from: "",
  main_phone: "",
  fax: "",
  currency: "PHP",
  terms: "",
  tax_id: "",
  tin: "",
  status: "active",
};

const VendorEnlistment = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [importing, setImporting] = useState(false);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [disableTarget, setDisableTarget] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkDisable, setShowBulkDisable] = useState(false);

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/accounting/vendors`, {
        credentials: "include",
      });
      const data = await res.json();
      if (data.success) setList(data.data || []);
    } catch (err) {
      console.error("fetch vendors", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const method = editingId ? "PUT" : "POST";
    const url = editingId
      ? `${API_BASE_URL}/api/accounting/vendors/${editingId}`
      : `${API_BASE_URL}/api/accounting/vendors`;
    try {
      const res = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) {
        setForm(emptyForm);
        setEditingId(null);
        fetchVendors();
        setShowModal(false);
      } else {
        alert(data.message || "Save failed");
      }
    } catch (err) {
      console.error("save vendor", err);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (row) => {
    setEditingId(row.id);
    setForm({
      vendor: row.vendor || "",
      account_no: row.account_no || "",
      bill_from: row.bill_from || "",
      main_phone: row.main_phone || "",
      fax: row.fax || "",
      currency: row.currency || "PHP",
      terms: row.terms || "",
      tax_id: row.tax_id || "",
      tin: row.tin || "",
      status: row.status || "active",
    });
    setShowModal(true);
  };

  const handleDeactivateRequest = (row) => {
    setDisableTarget(row);
  };

  const confirmDeactivate = async () => {
    if (!disableTarget?.id) return;
    await authFetch(`${API_BASE_URL}/api/accounting/vendors/${disableTarget.id}`, {
      method: "DELETE",
      credentials: "include",
    });
    setDisableTarget(null);
    fetchVendors();
  };

  const handleFilePick = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      if (!rows.length) {
        alert("No rows found in CSV.");
        return;
      }
      setPreviewRows(rows);
      setShowImportModal(true);
    } catch (err) {
      console.error("read csv", err);
      alert("Could not read CSV file.");
    }
  };

  const confirmImport = async () => {
    if (!previewRows.length) return;
    setImporting(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/accounting/vendors/import`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ items: previewRows }),
      });
      const data = await res.json();
      if (!data.success) {
        alert(data.message || "Import failed");
      } else {
        setShowImportModal(false);
        setPreviewRows([]);
        fetchVendors();
      }
    } catch (err) {
      console.error("import", err);
      alert("Import failed");
    } finally {
      setImporting(false);
    }
  };

  const filtered = list.filter((row) => {
    const term = search.toLowerCase();
    if (!term) return true;
    return (
      row.vendor?.toLowerCase().includes(term) ||
      row.account_no?.toLowerCase().includes(term) ||
      row.tin?.toLowerCase().includes(term)
    );
  });

  const toggleSelectAll = () => {
    if (selectedIds.length === filtered.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filtered.map((r) => r.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  return (
    <div className="space-y-6 font-poppins">
      <div className="bg-white/95 backdrop-blur-sm border border-gray-100 rounded-3xl shadow-sm p-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.14em] text-gray-500">Accounting</p>
          <h1 className="text-2xl font-semibold text-blue">Vendor Enlistment</h1>
          <p className="text-sm text-gray-600">Create, edit, and import vendors.</p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <button
            type="button"
            onClick={() => {
              setEditingId(null);
              setForm(emptyForm);
              setShowModal(true);
            }}
            className="px-4 py-2 rounded-lg bg-blue text-white text-sm font-semibold hover:bg-blue/90 shadow-sm"
          >
            + Add Vendor
          </button>
          <label className="cursor-pointer text-sm font-semibold text-blue underline">
            Import CSV
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => handleFilePick(e.target.files?.[0])}
              disabled={importing}
            />
          </label>
          <button
            type="button"
            onClick={fetchVendors}
            className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:border-gray-300"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="bg-white/95 rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="font-semibold text-blue">Vendor List</h3>
            <p className="text-xs text-gray-500">Total: {filtered.length}</p>
          </div>
          <div className="flex items-center gap-3">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search vendor, account no, TIN"
              className="rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-blue focus:ring-1 focus:ring-blue outline-none"
            />
            {loading && <span className="text-sm text-gray-500">Loading…</span>}
            <button
              type="button"
              disabled={!selectedIds.length}
              onClick={() => setShowBulkDisable(true)}
              className="px-3 py-2 rounded-lg border border-red/30 text-red text-xs font-semibold hover:bg-red/10 disabled:opacity-50"
            >
              Delete Selected ({selectedIds.length})
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-max min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr className="text-left uppercase text-[11px] tracking-wide text-gray-500">
                <th className="px-5 py-3 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={selectedIds.length === filtered.length && filtered.length > 0}
                    onChange={toggleSelectAll}
                    className="w-4 h-4"
                  />
                </th>
                <th className="px-5 py-3 whitespace-nowrap min-w-[150px]">Vendor</th>
                <th className="px-5 py-3 whitespace-nowrap min-w-[120px]">Account No.</th>
                <th className="px-5 py-3 whitespace-nowrap min-w-[250px]">Bill From</th>
                <th className="px-5 py-3 whitespace-nowrap min-w-[120px]">Main Phone</th>
                <th className="px-5 py-3 whitespace-nowrap min-w-[100px]">Fax</th>
                <th className="px-5 py-3 whitespace-nowrap min-w-[80px]">Currency</th>
                <th className="px-5 py-3 whitespace-nowrap min-w-[100px]">Terms</th>
                <th className="px-5 py-3 whitespace-nowrap min-w-[120px]">Tax ID</th>
                <th className="px-5 py-3 whitespace-nowrap min-w-[120px]">TIN</th>
                <th className="px-5 py-3 whitespace-nowrap text-right min-w-[150px]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50/80">
                  <td className="px-5 py-3 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(row.id)}
                      onChange={() => toggleSelect(row.id)}
                      className="w-4 h-4"
                    />
                  </td>
                  <td className="px-5 py-3 whitespace-nowrap font-medium text-gray-800">{row.vendor}</td>
                  <td className="px-5 py-3 whitespace-nowrap text-gray-700">{row.account_no}</td>
                  <td className="px-5 py-3 whitespace-nowrap text-gray-700">{row.bill_from}</td>
                  <td className="px-5 py-3 whitespace-nowrap text-gray-700">{row.main_phone}</td>
                  <td className="px-5 py-3 whitespace-nowrap text-gray-700">{row.fax}</td>
                  <td className="px-5 py-3 whitespace-nowrap text-gray-700">{row.currency}</td>
                  <td className="px-5 py-3 whitespace-nowrap text-gray-700">{row.terms}</td>
                  <td className="px-5 py-3 whitespace-nowrap text-gray-700">{row.tax_id}</td>
                  <td className="px-5 py-3 whitespace-nowrap text-gray-700">{row.tin}</td>
                  <td className="px-5 py-3 whitespace-nowrap text-right space-x-2">
                    <button
                      onClick={() => handleEdit(row)}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-blue/30 text-blue text-xs font-semibold hover:bg-blue/10"
                    >
                      <i className="fa-solid fa-pen"></i> Edit
                    </button>
                    <button
                      onClick={() => handleDeactivateRequest(row)}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-red/30 text-red text-xs font-semibold hover:bg-red/10"
                    >
                      <i className="fa-solid fa-trash"></i> Delete
                    </button>
                  </td>
                </tr>
              ))}
              {!filtered.length && (
                <tr>
                  <td className="px-5 py-5 text-center text-gray-500" colSpan={11}>
                    No vendors found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal &&
        ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl p-6 relative">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              <i className="fa-solid fa-xmark text-lg"></i>
            </button>
            <h2 className="text-xl font-semibold text-blue mb-4">
              {editingId ? "Update Vendor" : "Add Vendor"}
            </h2>
            <form
              onSubmit={handleSubmit}
              className="grid grid-cols-1 md:grid-cols-2 gap-4"
            >
              <div className="flex flex-col">
                <label className="text-xs text-gray-500 mb-1">Vendor *</label>
                <input
                  required
                  value={form.vendor}
                  onChange={(e) => setForm((f) => ({ ...f, vendor: e.target.value }))}
                  className="rounded-lg border border-gray-200 px-3 py-2 focus:border-blue focus:ring-1 focus:ring-blue outline-none text-sm"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-xs text-gray-500 mb-1">Account No.</label>
                <input
                  value={form.account_no}
                  onChange={(e) => setForm((f) => ({ ...f, account_no: e.target.value }))}
                  className="rounded-lg border border-gray-200 px-3 py-2 focus:border-blue focus:ring-1 focus:ring-blue outline-none text-sm"
                />
              </div>
              <div className="flex flex-col md:col-span-2">
                <label className="text-xs text-gray-500 mb-1">Bill From</label>
                <input
                  value={form.bill_from}
                  onChange={(e) => setForm((f) => ({ ...f, bill_from: e.target.value }))}
                  className="rounded-lg border border-gray-200 px-3 py-2 focus:border-blue focus:ring-1 focus:ring-blue outline-none text-sm"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-xs text-gray-500 mb-1">Main Phone</label>
                <input
                  value={form.main_phone}
                  onChange={(e) => setForm((f) => ({ ...f, main_phone: e.target.value }))}
                  className="rounded-lg border border-gray-200 px-3 py-2 focus:border-blue focus:ring-1 focus:ring-blue outline-none text-sm"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-xs text-gray-500 mb-1">Fax</label>
                <input
                  value={form.fax}
                  onChange={(e) => setForm((f) => ({ ...f, fax: e.target.value }))}
                  className="rounded-lg border border-gray-200 px-3 py-2 focus:border-blue focus:ring-1 focus:ring-blue outline-none text-sm"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-xs text-gray-500 mb-1">Currency</label>
                <select
                  value={form.currency}
                  onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
                  className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
                >
                  <option value="PHP">PHP</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
              <div className="flex flex-col">
                <label className="text-xs text-gray-500 mb-1">Terms</label>
                <input
                  value={form.terms}
                  onChange={(e) => setForm((f) => ({ ...f, terms: e.target.value }))}
                  className="rounded-lg border border-gray-200 px-3 py-2 focus:border-blue focus:ring-1 focus:ring-blue outline-none text-sm"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-xs text-gray-500 mb-1">Tax ID</label>
                <input
                  value={form.tax_id}
                  onChange={(e) => setForm((f) => ({ ...f, tax_id: e.target.value }))}
                  className="rounded-lg border border-gray-200 px-3 py-2 focus:border-blue focus:ring-1 focus:ring-blue outline-none text-sm"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-xs text-gray-500 mb-1">TIN</label>
                <input
                  value={form.tin}
                  onChange={(e) => setForm((f) => ({ ...f, tin: e.target.value }))}
                  className="rounded-lg border border-gray-200 px-3 py-2 focus:border-blue focus:ring-1 focus:ring-blue outline-none text-sm"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-xs text-gray-500 mb-1">Status</label>
                <select
                  value={form.status}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                  className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
                >
                  <option value="active">active</option>
                  <option value="inactive">inactive</option>
                </select>
              </div>
              <div className="md:col-span-2 flex gap-3 mt-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue text-white rounded-lg text-sm font-semibold hover:bg-blue/90 shadow-sm disabled:opacity-60"
                >
                  {saving ? "Saving..." : editingId ? "Update Vendor" : "Create Vendor"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditingId(null);
                    setForm(emptyForm);
                    setShowModal(false);
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {disableTarget &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative">
              <button
                onClick={() => setDisableTarget(null)}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark text-lg"></i>
              </button>
              <h3 className="text-lg font-semibold text-blue mb-2">Delete Vendor</h3>
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to delete <span className="font-semibold text-gray-800">{disableTarget.vendor}</span>?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={confirmDeactivate}
                  className="px-4 py-2 bg-red text-white rounded-lg text-sm font-semibold hover:bg-red/90 shadow-sm"
                >
                  Delete
                </button>
                <button
                  onClick={() => setDisableTarget(null)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {showImportModal &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl p-6 relative">
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setPreviewRows([]);
                }}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark text-lg"></i>
              </button>
              <h3 className="text-lg font-semibold text-blue mb-3">Import Preview ({previewRows.length} rows)</h3>
              <div className="border border-gray-100 rounded-xl mb-4 max-h-80 overflow-x-auto overflow-y-auto">
                <table className="text-sm w-max">
                  <thead className="bg-gray-50 text-gray-700 sticky top-0 z-10">
                    <tr className="text-left uppercase text-[11px] tracking-wide text-gray-500">
                      <th className="px-4 py-2 whitespace-nowrap">Vendor</th>
                      <th className="px-4 py-2 whitespace-nowrap">Account No.</th>
                      <th className="px-4 py-2 whitespace-nowrap">Bill From</th>
                      <th className="px-4 py-2 whitespace-nowrap">Main Phone</th>
                      <th className="px-4 py-2 whitespace-nowrap">Fax</th>
                      <th className="px-4 py-2 whitespace-nowrap">Currency</th>
                      <th className="px-4 py-2 whitespace-nowrap">Terms</th>
                      <th className="px-4 py-2 whitespace-nowrap">Tax ID</th>
                      <th className="px-4 py-2 whitespace-nowrap">TIN</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {previewRows.map((row, idx) => (
                      <tr key={idx} className="hover:bg-gray-50/80">
                        <td className="px-4 py-2 whitespace-nowrap">{row.vendor}</td>
                        <td className="px-4 py-2 whitespace-nowrap">{row.account_no}</td>
                        <td className="px-4 py-2 whitespace-nowrap max-w-[300px] truncate">{row.bill_from}</td>
                        <td className="px-4 py-2 whitespace-nowrap">{row.main_phone}</td>
                        <td className="px-4 py-2 whitespace-nowrap">{row.fax}</td>
                        <td className="px-4 py-2 whitespace-nowrap">{row.currency || "PHP"}</td>
                        <td className="px-4 py-2 whitespace-nowrap">{row.terms}</td>
                        <td className="px-4 py-2 whitespace-nowrap">{row.tax_id}</td>
                        <td className="px-4 py-2 whitespace-nowrap">{row.tin}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={confirmImport}
                  disabled={importing}
                  className="px-4 py-2 bg-blue text-white rounded-lg text-sm font-semibold hover:bg-blue/90 shadow-sm disabled:opacity-60"
                >
                  {importing ? "Importing..." : "Confirm Import"}
                </button>
                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setPreviewRows([]);
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {showBulkDisable &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative">
              <button
                onClick={() => setShowBulkDisable(false)}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark text-lg"></i>
              </button>
              <h3 className="text-lg font-semibold text-blue mb-2">Delete Selected Vendors</h3>
              <p className="text-sm text-gray-600 mb-4">
                Delete {selectedIds.length} selected vendors?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={async () => {
                    setShowBulkDisable(false);
                    setImporting(true);
                    try {
                      await Promise.all(
                        selectedIds.map((id) =>
                          authFetch(`${API_BASE_URL}/api/accounting/vendors/${id}`, {
                            method: "DELETE",
                          })
                        )
                      );
                      setSelectedIds([]);
                      fetchVendors();
                    } finally {
                      setImporting(false);
                    }
                  }}
                  className="px-4 py-2 bg-red text-white rounded-lg text-sm font-semibold hover:bg-red/90 shadow-sm"
                >
                  Delete
                </button>
                <button
                  onClick={() => setShowBulkDisable(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

// Map CSV headers to database field names
const headerMap = {
  "vendor": "vendor",
  "account no.": "account_no",
  "account no": "account_no",
  "bill from": "bill_from",
  "main phone": "main_phone",
  "fax": "fax",
  "currency": "currency",
  "terms": "terms",
  "tax id": "tax_id",
  "tin": "tin",
};

// Parse CSV line handling quoted fields with commas
function parseCSVLine(line) {
  const result = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === "," && !inQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
}

function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter(Boolean);
  if (!lines.length) return [];

  const headers = parseCSVLine(lines[0]).map((h) => h.toLowerCase());

  return lines.slice(1).map((line) => {
    const cols = parseCSVLine(line);
    const obj = {};

    headers.forEach((h, idx) => {
      const mappedField = headerMap[h];
      if (mappedField) {
        obj[mappedField] = cols[idx] || "";
      }
    });

    return obj;
  });
}

export default VendorEnlistment;
